import React from "react";
import SEO from "../components/SEO";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: [
      "When you visit our website, make a reservation, place an order, or contact us, we may collect the following types of information:",
      "• Personal Identification Information: Name, email address, phone number, and postal address.",
      "• Transaction Information: Details of orders or reservations you place through our site.",
      "• Usage Data: IP address, browser type, pages visited, time spent on pages, and referring URLs.",
      "• Communications: Records of your correspondence with us, including feedback and enquiries.",
    ],
  },
  {
    title: "2. How We Use Your Information",
    body: [
      "We use the information we collect for the following purposes:",
      "• To process reservations, orders, and payments.",
      "• To communicate with you regarding your bookings, queries, or complaints.",
      "• To send promotional emails, newsletters, and offers — only if you have opted in.",
      "• To improve our website, services, and customer experience.",
      "• To comply with legal obligations and enforce our Terms & Conditions.",
    ],
  },
  {
    title: "3. Cookies & Tracking Technologies",
    body: [
      "Our website uses cookies — small text files placed on your device — to enhance your browsing experience. Cookies help us remember your preferences, understand how you use our site, and deliver relevant content.",
      "You can control cookies through your browser settings. Please note that disabling cookies may affect certain features of our website.",
    ],
  },
  {
    title: "4. Sharing of Information",
    body: [
      "We do not sell, trade, or rent your personal information to third parties. We may share your data with trusted service providers who assist us in operating our website and conducting business, provided they agree to keep this information confidential.",
      "We may also disclose your information when required by law, court order, or governmental authority, or to protect the rights, property, or safety of Barbequeen, its customers, or others.",
    ],
  },
  {
    title: "5. Data Retention",
    body: [
      "We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, including legal, accounting, or reporting obligations.",
      "When your data is no longer required, we will securely delete or anonymise it.",
    ],
  },
  {
    title: "6. Data Security",
    body: [
      "We take reasonable precautions to protect your personal information against unauthorised access, disclosure, alteration, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.",
      "We encourage you to use strong passwords and to be vigilant about protecting your own personal data.",
    ],
  },
  {
    title: "7. Third-Party Links",
    body: [
      "Our website may contain links to third-party websites. We have no control over the content or privacy practices of those sites and are not responsible for their policies. We encourage you to review the privacy policy of every website you visit.",
    ],
  },
  {
    title: "8. Children's Privacy",
    body: [
      "Our services are not directed to children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately so we can delete it.",
    ],
  },
  {
    title: "9. Your Rights",
    body: [
      "You have the right to:",
      "• Access the personal data we hold about you.",
      "• Request correction of inaccurate data.",
      "• Request deletion of your data, subject to legal obligations.",
      "• Withdraw consent to marketing communications at any time.",
      "To exercise any of these rights, please contact us at the details below.",
    ],
  },
  {
    title: "10. Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.",
    ],
  },
  {
    title: "11. Contact Us",
    body: [
      "If you have any questions or concerns about this Privacy Policy, please contact us:",
      "Nonvee Foods Private Limited",
      "# 32, Valmiki Street, Near Ramakrishna Higher Secondary School,",
      "Subramaniya Nagar, Salem – 636 005.",
      "Email: info@barbequeen.com",
      "Phone: +91 95972 28788",
    ],
  },
];

const PrivacyPolicyPage = () => {
  const lastUpdated = "15 July 2026";

  return (
    <div
      className="min-h-screen bg-[#f9f5f0]"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <SEO
        title="Privacy Policy | Barbequeen Restaurant"
        metaTitle="Privacy Policy | Barbequeen Restaurant"
        description="Read the Barbequeen Restaurant Privacy Policy to understand how Nonvee Foods Private Limited collects, uses, and protects your personal data."
        keywords="Barbequeen privacy policy, customer privacy, data policy Barbequeen"
        robots="index, follow"
        canonical="https://barbequeen.com/privacy-policy"
        og={{
          type: "website",
          url: "https://barbequeen.com/privacy-policy",
          title: "Privacy Policy | Barbequeen Restaurant",
          description: "Read the Barbequeen Privacy Policy to learn how we protect your personal information.",
          image: "https://barbequeen.com/images/home-banner.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
      />
      {/* Hero Banner */}
      <section className="relative overflow-hidden" style={{ minHeight: "320px" }}>
        <img
          src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1600&auto=format&fit=crop"
          alt="Privacy Policy"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(10,0,2,0.75) 0%, rgba(10,0,2,0.88) 100%)" }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">Legal</p>
          <h1
            className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed">
            Your privacy matters to us. Learn how we collect, use, and protect your information.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-16">

        {/* Intro card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#9b1c2e]/10 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-[#9b1c2e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-700 leading-relaxed text-sm">
                Barbequeen (operated by <strong>Nonvee Foods Private Limited</strong>) is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you visit our website or use our services.
              </p>
              <p className="mt-3 text-xs text-gray-400">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-6 bg-[#9b1c2e] rounded-full" />
                <h2
                  className="text-lg font-bold text-gray-900"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {section.title}
                </h2>
              </div>
              <div className="space-y-3">
                {section.body.map((para, i) => (
                  <p key={i} className="text-sm text-gray-600 leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

      </section>
    </div>
  );
};

export default PrivacyPolicyPage;

